import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { FaExclamationTriangle, FaInfoCircle, FaLink, FaLock, FaTrash } from 'react-icons/fa';
import { useLabels } from '../../hooks/useLabels';
import { toast } from '../../toasts';
import { getError } from '../../utils';
import { Button, controlClass } from '../EntityList';
import { formatAmount } from './describeRow';

/**
 * Preview and exact server-label confirmation for a transactional cascade
 * delete — the generic form of DebtDeletionModal / AccountDeletionModal.
 *
 * The server builds the plan (GET /:id/deletion-preview) and the same body
 * comes back on a 409, so the modal renders one shape either way:
 * mainEntities (itemised rows), generatedEntities (counts), preserved,
 * unlinked (counts of records that only lose a reference), warnings,
 * conflicts. Nothing is individually acknowledged — the single gate is typing
 * the server-returned label.
 */
function CascadeDeletionModal({
	show,
	entityId,
	service,
	rootKey,
	titleKey,
	deletedMsgKey,
	introKey,
	noticeKey,
	sections: sectionConfig,
	onClose,
	onDeleted,
}) {
	const { t } = useLabels();
	const [state, setState] = useState('loading');
	const [preview, setPreview] = useState(null);
	const [error, setError] = useState('');
	const [confirmLabel, setConfirmLabel] = useState('');

	const rootLabel = preview?.[rootKey]?.label || '';
	const previewRequest = useRef(0);
	const submitting = useRef(false);

	const loadPreview = useCallback(async () => {
		const request = ++previewRequest.current;
		setState('loading');
		setPreview(null);
		setConfirmLabel('');
		setError('');
		try {
			const { data } = await service.getDeletionPreview(entityId);
			if (request !== previewRequest.current) return;
			setPreview(data);
			setState(data.blocked ? 'blocked' : 'idle');
		} catch (err) {
			if (request !== previewRequest.current) return;
			setError(getError(err));
			setState('error');
		}
	}, [entityId, service]);

	useEffect(() => {
		if (show && entityId) loadPreview();
		return () => { previewRequest.current++; };
	}, [show, entityId, loadPreview]);

	// Reset on exit rather than on close, so the body doesn't flip back to a
	// spinner while the modal is still fading out.
	const handleExited = () => {
		setPreview(null);
		setConfirmLabel('');
		setState('loading');
		setError('');
	};

	const sections = useMemo(() => {
		if (!preview) return [];
		return sectionConfig.map((section) => ({
			...section,
			label: t(section.labelKey),
			items: preview[section.source || 'mainEntities']?.[section.key] || [],
		})).filter((section) => section.items.length > 0);
	}, [preview, sectionConfig, t]);

	const generated = preview?.generatedEntities || [];
	const unlinked = preview?.unlinked || [];

	const labelMatches = rootLabel !== '' && confirmLabel === rootLabel;
	const canDelete = show && state === 'idle' && String(preview?.[rootKey]?._id) === String(entityId) && labelMatches;

	const handleDelete = async () => {
		if (!canDelete || submitting.current) return;
		submitting.current = true;
		setState('deleting');
		try {
			await service.delete(entityId);
		} catch (err) {
			// A 409 carries a fresh preview: the graph changed since we loaded it,
			// so re-render from the response instead of just toasting.
			const data = err?.response?.data;
			if (err?.response?.status === 409 && data?.totals) {
				setPreview(data);
				setConfirmLabel('');
				setState(data.blocked ? 'blocked' : 'idle');
			} else {
				setState('idle');
			}
			toast.error(getError(err));
			return;
		} finally {
			submitting.current = false;
		}
		toast.success(t(deletedMsgKey));
		onDeleted();
	};

	const busy = state === 'deleting';
	const intro = introKey
		? t(introKey)
		: t(sections.length === 0 && generated.length === 0 ? 'msg.cascadeEmpty' : 'msg.cascadeIntro');

	return (
		<BsModal
			show={show}
			onHide={busy ? undefined : onClose}
			onExited={handleExited}
			centered
			scrollable
			backdrop={busy ? 'static' : true}
			keyboard={!busy}
			dialogClassName='tw:max-w-[600px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton={!busy}
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-danger-soft tw:text-danger'>
						{state === 'blocked' ? <FaLock /> : <FaTrash />}
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t(titleKey)}
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>{rootLabel}</p>
					</div>
				</div>
			</BsModal.Header>

			{state === 'loading' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-accent' />
					<div className='tw:text-[12.5px] tw:text-ink-3'>Analizando registros relacionados…</div>
				</BsModal.Body>
			)}

			{state === 'deleting' && (
				<BsModal.Body className='tw:flex tw:flex-col tw:items-center tw:gap-1 tw:px-[22px] tw:py-12 tw:text-center'>
					<div className='tw:mb-3 tw:h-14 tw:w-14 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-danger' />
					<div className='tw:font-semibold tw:text-ink-0'>{t('msg.cascadeDeleting')}</div>
					<div className='tw:text-[12.5px] tw:text-ink-3'>No cierre esta ventana</div>
				</BsModal.Body>
			)}

			{state === 'error' && (
				<>
					<BsModal.Body className='tw:px-[22px] tw:py-[18px]'>
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-3 tw:py-2.5 tw:text-[12.5px] tw:text-danger'>
							{error || t('msg.cascadePreviewFailed')}
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							{t('action.cancel')}
						</Button>
						<Button variant='primary' onClick={loadPreview}>
							Reintentar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'blocked' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<div className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12.5px] tw:text-ink-1'>
							{preview?.totals?.blockedBySize
								? `Tiene ${preview.totals.documentCount} registros relacionados, más de los ${preview.totals.maxDocumentCount} que se pueden eliminar en una sola operación. Reduzca los registros relacionados antes de volver a intentar.`
								: t('msg.cascadeBlocked')}
						</div>
						{(preview?.conflicts || []).length > 0 && (
							// Capped so the explanation above and the footer stay in view no
							// matter how many records are blocking.
							<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
								{preview.conflicts.map((conflict) => (
									<div
										key={`${conflict.code}:${conflict._id}`}
										className='tw:flex tw:items-start tw:gap-2.5 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2.5 tw:last:border-b-0'
									>
										<span className='tw:mt-0.5 tw:shrink-0 tw:text-warn'>
											<FaExclamationTriangle />
										</span>
										<div>
											<div className='tw:text-[13.5px] tw:font-medium tw:text-ink-0'>{conflict.label}</div>
											<div className='tw:text-[11.5px] tw:text-ink-3'>{conflict.message}</div>
										</div>
									</div>
								))}
							</div>
						)}
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							Cerrar
						</Button>
					</BsModal.Footer>
				</>
			)}

			{state === 'idle' && (
				<>
					<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
						<p className='tw:mb-0 tw:text-[13px] tw:text-ink-2'>{intro}</p>

						{noticeKey && (
							<div className='tw:flex tw:items-start tw:gap-2.5 tw:rounded-[7px] tw:border tw:border-accent tw:bg-accent-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'>
								<span className='tw:mt-0.5 tw:shrink-0 tw:text-accent'>
									<FaInfoCircle />
								</span>
								<span>{t(noticeKey)}</span>
							</div>
						)}

						{(preview?.warnings || []).map((warning) => (
							<div
								key={warning.code}
								className='tw:rounded-[7px] tw:border tw:border-warn tw:bg-warn-soft tw:px-3 tw:py-2.5 tw:text-[12px] tw:text-ink-1'
							>
								{warning.code === 'COLLECTED_PAYMENTS'
									? t('msg.cascadeCollectedPayments')
										.replace('{count}', warning.paymentCount)
										.replace('{total}', (warning.totalCollected || []).map(({ currency, amount }) => formatAmount(amount, currency)).join(' · ') || '0')
										.replace('{paidDueCount}', warning.paidDueCount)
									: warning.message}
							</div>
						))}

						{sections.map((section) => (
							<div key={`${section.source || 'mainEntities'}:${section.key}`}>
								<div className='tw:mb-2 tw:flex tw:items-center tw:justify-between'>
									<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
										{section.label}
									</span>
									<span className='tw:text-[11px] tw:text-ink-3'>{section.items.length}</span>
								</div>
								<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{section.items.map((item) => {
										const detail = section.describe ? section.describe(item, t) : '';
										return (
											<div
												key={item._id}
												className='tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
											>
												<div className='tw:truncate tw:text-[13.5px] tw:text-ink-0'>{item.label}</div>
												{detail && <div className='tw:text-[11.5px] tw:text-ink-3'>{detail}</div>}
											</div>
										);
									})}
								</div>
							</div>
						))}

						{generated.length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.cascadeGenerated')}
								</div>
								<div className='tw:max-h-[320px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{generated.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:flex-1 tw:text-[13.5px] tw:text-ink-0'>{group.label}</span>
											<span className='tw:text-[13px] tw:font-semibold tw:text-ink-1'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{unlinked.length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.cascadeUnlinked')}
								</div>
								<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface'>
									{unlinked.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:shrink-0 tw:text-ink-3'>
												<FaLink size={11} />
											</span>
											<span className='tw:flex-1 tw:text-[13.5px] tw:text-ink-0'>{group.label}</span>
											<span className='tw:text-[13px] tw:font-semibold tw:text-ink-1'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						{(preview?.preserved || []).length > 0 && (
							<div>
								<div className='tw:mb-2 tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'>
									{t('msg.cascadePreserved')}
								</div>
								<div className='tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface-2'>
									{preview.preserved.map((group) => (
										<div
											key={group.key}
											className='tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-border tw:px-3 tw:py-2 tw:last:border-b-0'
										>
											<span className='tw:shrink-0 tw:text-ink-3'>
												<FaLock size={11} />
											</span>
											<div className='tw:flex-1'>
												<span className='tw:text-[13px] tw:text-ink-1'>{group.label}</span>
												<span className='tw:ml-2 tw:text-[11.5px] tw:text-ink-3'>{group.reason}</span>
											</div>
											<span className='tw:text-[13px] tw:text-ink-2'>{group.count}</span>
										</div>
									))}
								</div>
							</div>
						)}

						<div className='tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:py-3'>
							<label htmlFor='cascade-delete-confirm' className='tw:mb-1.5 tw:block tw:text-[12.5px] tw:text-ink-1'>
								{t('msg.cascadeTypeLabel')}{' '}
								<span className='tw:font-semibold tw:text-ink-0'>{rootLabel}</span>
							</label>
							<input
								id='cascade-delete-confirm'
								type='text'
								autoComplete='off'
								autoFocus
								value={confirmLabel}
								onChange={(event) => setConfirmLabel(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter') handleDelete();
								}}
								className={`${controlClass} tw:w-full tw:px-2.5`}
							/>
							{confirmLabel.length > 0 && !labelMatches && (
								<div className='tw:mt-1.5 tw:text-[11.5px] tw:text-danger'>{t('msg.cascadeLabelMismatch')}</div>
							)}
						</div>
					</BsModal.Body>
					<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
						<Button variant='default' onClick={onClose}>
							{t('action.cancel')}
						</Button>
						<Button
							variant='primary'
							icon={<FaTrash />}
							disabled={!canDelete}
							className={!canDelete ? 'tw:opacity-50' : ''}
							onClick={handleDelete}
						>
							{t('action.delete')}
						</Button>
					</BsModal.Footer>
				</>
			)}
		</BsModal>
	);
}

CascadeDeletionModal.propTypes = {
	show: PropTypes.bool.isRequired,
	entityId: PropTypes.string,
	service: PropTypes.shape({
		getDeletionPreview: PropTypes.func.isRequired,
		delete: PropTypes.func.isRequired,
	}).isRequired,
	rootKey: PropTypes.string.isRequired,
	titleKey: PropTypes.string.isRequired,
	deletedMsgKey: PropTypes.string.isRequired,
	introKey: PropTypes.string,
	// Optional informational note shown under the intro (e.g. an alternative to deleting).
	noticeKey: PropTypes.string,
	sections: PropTypes.arrayOf(PropTypes.shape({
		key: PropTypes.string.isRequired,
		labelKey: PropTypes.string.isRequired,
		source: PropTypes.string,
		describe: PropTypes.func,
	})).isRequired,
	onClose: PropTypes.func.isRequired,
	onDeleted: PropTypes.func.isRequired,
};

export default CascadeDeletionModal;
